import React, { Component } from 'react';
import { t, dateLocale } from '../../i18n';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import '../../style/DisplayManagement.scss';
import '../../style/DisplaysList.scss';
import { Query } from 'react-apollo';
import queries from '../../queries';
import { FormattedMessage, injectIntl } from 'react-intl';
import NeedAdmin from '../NeedAdmin';
import confirmDialog from '../dialogs/ConfirmDialog';
import { apolloClient } from '../../config';
import { showToast } from '../../toast';

// En-tête du tableau (masqué en mobile via CSS). `onSort` reçoit le nom de colonne.
function DisplayListHeader({ onSort }) {
  return (
    <li className='tsk-displays-row'>
      <div className='tsk-displays-check' aria-hidden='true' />
      <div className={"tsk-displays-list-item"}>
        <div className='tsk-display-col-item bold'>Row</div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('name')}><FormattedMessage id="displays.list.colone.name" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('location')}><FormattedMessage id="displays.list.colone.location" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('project')}><FormattedMessage id="displays.list.colone.current" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('connected')}><FormattedMessage id="displays.list.colone.state" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('physical_id')}><FormattedMessage id="displays.list.colone.physical_id" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('last_time_connected')}><FormattedMessage id="displays.list.colone.last_connected" /></div>
        <div className='tsk-display-col-item bold'><FormattedMessage id="displays.list.colone.version" /></div>
        <div className='tsk-display-col-item bold'>Device group</div>
      </div>
    </li>
  );
}

// Ce que la borne a réellement installé (rapporté par elle-même après téléchargement), avec repli sur le
// numéro de version seul si le projet a depuis été supprimé, ou "None" si la borne n'a jamais publié/vidé
// son cache. Distinct de `device.project` (l'assignation en base, qui peut être différente ou périmée).
function installedLabel(device) {
  const install = device.currentInstall;
  if (!install || install.version == null) return 'None';
  const projectName = install.project && install.project.name;
  return projectName ? `${projectName} (v${install.version})` : `v${install.version}`;
}

// Date/heure de dernière connexion : "En ligne" si connectée, "—" si inconnue ou date invalide.
function lastConnectedLabel(device, onlineLabel) {
  if (device.connected) return onlineLabel;
  if (!device.last_time_connected) return '—';
  const date = new Date(device.last_time_connected);
  if (isNaN(date.getTime())) return '—';
  try {
    return date.toLocaleString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return date.toISOString();
  }
}

// Une ligne appareil. `data-label` (traduit) sert d'étiquette en mode carte (mobile).
function DisplayRow({ device, index, intl, checked, onToggle }) {
  const t = (id, fallback) => {
    try { return intl.formatMessage({ id }); } catch (e) { return fallback; }
  };
  return (
    <li className='tsk-displays-row' key={device.id}>
      {/* Case hors du lien : cocher un écran ne doit pas ouvrir sa fiche */}
      <label className='tsk-displays-check' title={t('displays.bulk.select_one', 'Select this screen')}>
        <input type='checkbox' checked={checked} onChange={() => onToggle(device.id)} aria-label={(device.name == null) ? 'No name' : device.name} />
      </label>
      <Link to={'/admin/displays/' + device.id} className={'tsk-displays-list-item'}>
        <div className='tsk-display-col-item tsk-col-hide-mobile' data-label="#">{index + 1}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.name', 'Device name')}>{(device.name == null) ? 'No name' : device.name}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.location', 'Location')}>{device.location == null ? 'No location' : device.location}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.current', 'Project')}>{(device.project == null) ? 'No project' : device.project ? device.project.name : 'No project'}</div>
        <div className={'tsk-display-col-item tsk-col-connected ' + (device.connected ? 'is-connected' : 'is-offline')} data-label={t('displays.list.colone.state', 'Connected')}>{device.connected ? "Yes" : "No"}</div>
        <div className='tsk-display-col-item tsk-col-hide-mobile' data-label={t('displays.list.colone.physical_id', 'Physical ID')}>{device.physical_id}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.last_connected', 'Last connection')}>{lastConnectedLabel(device, t('displays.list.last_connected.online', 'Online'))}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.version', 'Version')}>{installedLabel(device)}</div>
        <div className='tsk-display-col-item tsk-col-hide-mobile' data-label="Device group">{(device.devicesGroup) ? device.devicesGroup.name : 'No group'}</div>
      </Link>
    </li>
  );
}

class DisplaysList extends Component {

    constructor(props) {
        super(props);
        this.state = {
          sortColumn: 'name',
          sortOrder: 'asc', // 'asc' for ascending, 'desc' for descending
          search: "",
          connectedFilter: 'all', // 'all' | 'connected' | 'offline'
          group_id: null,
          selected: {},   // id d'écran -> true (cases cochées)
          clearing: false, // vidage de cache en cours
          projectId: '',   // interface choisie pour l'assignation groupée
          projectName: '',
          projectFilter: '',
          assigning: false,
        };
      }

      handleSort = (column) => {
        const { sortColumn, sortOrder } = this.state;

        if (sortColumn === column) {
          // Si vous cliquez sur la même colonne, basculez l'ordre de tri
          this.setState({
            sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
          });
        } else {
          // Si vous cliquez sur une nouvelle colonne, triez par ordre alphabétique ascendant par défaut
          this.setState({
            sortColumn: column,
            sortOrder: 'asc',
          });
        }
      };

      // Tri partagé par les trois variantes de route.
      sortDevices = (devices) => {
        const { sortOrder, sortColumn } = this.state;
        return devices.slice().sort((a, b) => {
          const aValue = a[sortColumn];
          const bValue = b[sortColumn];

          if (sortColumn === 'project') {
            if (aValue !== null && bValue !== null) {
              const aProjectName = a.project && a.project.name ? a.project.name : '';
              const bProjectName = b.project && b.project.name ? b.project.name : '';
              return sortOrder === 'asc' ? aProjectName.localeCompare(bProjectName) : bProjectName.localeCompare(aProjectName);
            }
            return 0;
          }
          if (sortColumn === 'name' || sortColumn === 'physical_id') {
            const aProjectName = aValue || 'no name';
            const bProjectName = bValue || 'no name';
            return sortOrder === 'asc' ? aProjectName.localeCompare(bProjectName) : bProjectName.localeCompare(aProjectName);
          }
          if (sortColumn === 'last_time_connected') {
            // En ligne = le plus récent ; inconnu = le plus ancien
            const time = (d) => d.connected ? Number.MAX_SAFE_INTEGER : (d.last_time_connected ? (new Date(d.last_time_connected).getTime() || 0) : 0);
            return sortOrder === 'asc' ? time(a) - time(b) : time(b) - time(a);
          }
          if (sortColumn === 'connected') {
            const aProjectName = aValue ? 'yes' : 'no';
            const bProjectName = bValue ? 'yes' : 'no';
            return sortOrder === 'asc' ? aProjectName.localeCompare(bProjectName) : bProjectName.localeCompare(aProjectName);
          }
          return false;
        });
      };

      // Filtre recherche (toutes colonnes, insensible à la casse).
      matchesSearch = (device) => {
        const lowerSearch = this.state.search.toLowerCase();
        return (
          (device.name && device.name.toLowerCase().includes(lowerSearch)) ||
          (device.project && device.project.name && device.project.name.toLowerCase().includes(lowerSearch)) ||
          (device.physical_id && device.physical_id.toLowerCase().includes(lowerSearch)) ||
          installedLabel(device).toLowerCase().includes(lowerSearch) ||
          (device.connected ? "Yes" : "No").toLowerCase().includes(lowerSearch) ||
          (device.location && device.location.toLowerCase().includes(lowerSearch))
        );
      };

      // Filtre statut de connexion (tous / connectés / hors-ligne).
      matchesConnectedFilter = (device) => {
        const { connectedFilter } = this.state;
        if (connectedFilter === 'connected') return !!device.connected;
        if (connectedFilter === 'offline') return !device.connected;
        return true;
      };

      // Coquille commune : barre d'outils + contenu (barre d'actions + tableau) rendu par renderBody().
      renderShell(renderBody) {
        return (
          <NeedAdmin trueAdmin>
            <div className="tsk-displays-page">
              <div className="tsk-displays-toolbar">
                <button type="button" className='tsk-button tsk-displays-toolbar__back' onClick={() => { window.history.back() }}>Retour</button>
                <input className='tsk-input-search' type='text' placeholder={t('ui.search_ph')} value={this.state.search} onChange={(e) => { this.setState({ search: e.target.value }) }} />
                <select
                  className='tsk-displays-connected-filter'
                  value={this.state.connectedFilter}
                  onChange={(e) => { this.setState({ connectedFilter: e.target.value }) }}
                >
                  <option value='all'>Tous les appareils</option>
                  <option value='connected'>Connectés</option>
                  <option value='offline'>Hors-ligne</option>
                </select>
              </div>
              {renderBody()}
            </div>
          </NeedAdmin>
        );
      }

      // Écrans affichés : triés puis filtrés. `nameMustBeNull` est la particularité de la route 'noname'.
      visibleDevices(devices, nameMustBeNull = false) {
        return this.sortDevices(devices).filter((device) => {
          if (nameMustBeNull && device.name) return false;
          if (!this.matchesConnectedFilter(device)) return false;
          return this.matchesSearch(device);
        });
      }

      toggleSelected = (id) => {
        this.setState(({ selected }) => {
          const next = { ...selected };
          if (next[id]) delete next[id]; else next[id] = true;
          return { selected: next };
        });
      };

      // Coche / décoche tous les écrans actuellement affichés (les autres cases cochées sont conservées).
      toggleAllVisible = (visible, allChecked) => {
        this.setState(({ selected }) => {
          const next = { ...selected };
          visible.forEach((device) => { if (allChecked) delete next[device.id]; else next[device.id] = true; });
          return { selected: next };
        });
      };

      // Vide le cache des écrans cochés ET affichés. Seuls les écrans connectés reçoivent la commande (le serveur
      // ignore les autres en silence) : on ne compte donc que ceux-là. Un par un, pour ne pas surcharger l'API.
      clearCacheOf = (targets) => {
        const connected = targets.filter((device) => device.connected);
        if (connected.length === 0 || this.state.clearing) return;
        const skipped = targets.length - connected.length;
        const message = t('displays.bulk.confirm', { count: connected.length })
          + (skipped > 0 ? ' ' + t('displays.bulk.confirm_skipped', { count: skipped }) : '');
        confirmDialog(message).then(async () => {
          this.setState({ clearing: true });
          let failed = 0;
          for (const device of connected) {
            try {
              await apolloClient.mutate({ mutation: queries.displays.clear, variables: { id: device.id } });
            } catch (e) {
              failed += 1;
            }
          }
          this.setState({ clearing: false });
          if (failed === 0) showToast(t('displays.bulk.done', { count: connected.length }), 'success');
          else showToast(t('displays.bulk.partial', { failed, count: connected.length }), 'error', 6000);
        }, () => { /* annulé */ });
      };

      // Met la même interface sur tous les écrans cochés et affichés. Contrairement au vidage de cache, les écrans
      // hors-ligne sont aussi mis à jour : le serveur renvoie à la borne son interface (`projectId`) à sa reconnexion.
      assignInterfaceTo = (targets) => {
        const { projectId, projectName, assigning } = this.state;
        if (!projectId || targets.length === 0 || assigning) return;
        const offline = targets.filter((device) => !device.connected).length;
        const message = t('displays.bulk.assign_confirm', { count: targets.length, name: projectName })
          + (offline > 0 ? ' ' + t('displays.bulk.assign_confirm_offline', { count: offline }) : '');
        confirmDialog(message).then(async () => {
          this.setState({ assigning: true });
          let failed = 0;
          for (const device of targets) {
            try {
              await apolloClient.mutate({ mutation: queries.displays.update, variables: { input: { id: device.id, project_id: projectId } } });
            } catch (e) {
              failed += 1;
            }
          }
          this.setState({ assigning: false });
          try { await apolloClient.reFetchObservableQueries(); } catch (e) { /* la liste se mettra à jour au prochain chargement */ }
          if (failed === 0) showToast(t('displays.bulk.assign_done', { count: targets.length, name: projectName }), 'success');
          else showToast(t('displays.bulk.assign_partial', { failed, count: targets.length }), 'error', 6000);
        }, () => { /* annulé */ });
      };

      // Barre d'actions : tout sélectionner, compteur, bouton « Vider le cache ».
      renderBulkBar(visible) {
        const { selected, clearing, projectId, projectFilter, assigning } = this.state;
        const targets = visible.filter((device) => selected[device.id]);
        const connectedCount = targets.filter((device) => device.connected).length;
        const allChecked = visible.length > 0 && targets.length === visible.length;
        return (
          <div className='tsk-displays-bulk'>
            <label className='tsk-displays-bulk__all'>
              <input
                type='checkbox'
                checked={allChecked}
                disabled={visible.length === 0}
                onChange={() => this.toggleAllVisible(visible, allChecked)}
              />
              <span>{t('displays.bulk.select_all', 'Select all')}</span>
            </label>
            <span className='tsk-displays-bulk__count'>
              {targets.length > 0
                ? t('displays.bulk.selected', { count: targets.length, connected: connectedCount })
                : t('displays.bulk.none', 'No screen selected')}
            </span>
            <button
              type='button'
              className='tsk-button tsk-displays-bulk__clear'
              disabled={connectedCount === 0 || clearing}
              title={targets.length > 0 && connectedCount === 0 ? t('displays.bulk.all_offline') : undefined}
              onClick={() => this.clearCacheOf(targets)}
            >
              {clearing ? t('displays.bulk.clearing') : t('displays.bulk.clear_cache')}
            </button>
            <div className='tsk-displays-bulk__interface'>
              <Query query={queries.displays.projectNames}>
                {({ loading, error, data }) => {
                  if (loading) return <span className='tsk-displays-bulk__count'>{t('ui.common.loading')}</span>;
                  if (error) return <span className='tsk-displays-bulk__count'>{error.message}</span>;
                  const needle = projectFilter.trim().toLowerCase();
                  const options = data.projects
                    .filter((project) => !project.archived_at)
                    .filter((project) => !needle || (project.name || '').toLowerCase().includes(needle))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  return (
                    <React.Fragment>
                      <input
                        type='text'
                        className='tsk-input-lines tsk-displays-bulk__filter'
                        placeholder={t('displays.bulk.interface_filter', 'Filter interfaces')}
                        value={projectFilter}
                        onChange={(e) => this.setState({ projectFilter: e.target.value })}
                      />
                      <select
                        className='tsk-input-lines tsk-displays-bulk__select'
                        value={projectId}
                        onChange={(e) => {
                          const chosen = data.projects.find((project) => project.id === e.target.value);
                          this.setState({ projectId: e.target.value, projectName: chosen ? chosen.name : '' });
                        }}
                      >
                        <option value=''>{t('displays.bulk.interface_choose', 'Choose an interface…')}</option>
                        {options.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                      </select>
                    </React.Fragment>
                  );
                }}
              </Query>
              <button
                type='button'
                className='tsk-button tsk-displays-bulk__assign'
                disabled={!projectId || targets.length === 0 || assigning}
                onClick={() => this.assignInterfaceTo(targets)}
              >
                {assigning ? t('displays.bulk.assigning') : t('displays.bulk.assign', { count: targets.length })}
              </button>
            </div>
          </div>
        );
      }

      // Barre d'actions + tableau pour une liste d'appareils.
      renderTable(devices, nameMustBeNull = false) {
        const { intl } = this.props;
        const visible = this.visibleDevices(devices, nameMustBeNull);
        return (
          <React.Fragment>
            {this.renderBulkBar(visible)}
            <ul className='tsk-displays-list-container'>
              <DisplayListHeader onSort={this.handleSort} />
              {visible.map((device, index) => (
                <DisplayRow
                  key={device.id}
                  device={device}
                  index={index}
                  intl={intl}
                  checked={!!this.state.selected[device.id]}
                  onToggle={this.toggleSelected}
                />
              ))}
            </ul>
          </React.Fragment>
        );
      }

      render() {
        const { groupId } = this.props.match.params;

        if (groupId === 'noname') {
          return this.renderShell(() => (
            <Query query={queries.displays.list} variables={{ groupId: 'all' }}>
              {({ loading, error, data }) => {
                if (loading) return 'loading';
                if (error) return error.message;
                return this.renderTable(data.devices, true);
              }}
            </Query>
          ));
        }

        if (groupId !== 'all') {
          return this.renderShell(() => (
            <Query query={queries.displays.listByGroup} variables={{ id: groupId }}>
              {({ loading, error, data }) => {
                if (loading) return 'loading';
                if (error) return error.message;
                return this.renderTable(data.devicesGroup.devices);
              }}
            </Query>
          ));
        }

        return this.renderShell(() => (
          <Query query={queries.displays.list} variables={{ id: groupId }}>
            {({ loading, error, data }) => {
              if (loading) return 'loading';
              if (error) return error.message;
              return this.renderTable(data.devices);
            }}
          </Query>
        ));
      }
    }

export default injectIntl(DisplaysList);
